import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, RefreshCw, Plus, X, Key, Activity, Trash2, Clock, LogIn, Mail } from "lucide-react";

const STATUS_STYLES = {
  ready: "bg-[#DBEAFE] text-[#1E40AF]",
  active: "bg-[#D1FAE5] text-[#065F46]",
  expired: "bg-[#FEE2E2] text-[#991B1B]",
  logins_exhausted: "bg-[#FEE2E2] text-[#991B1B]",
  disabled: "bg-[#F3F4F6] text-[#4B5563]",
};

function CreateForm({ onCreated, onClose }) {
  const [form, setForm] = useState({
    email: "", password: "", full_name: "Demo User",
    max_logins: 5, expiry_days: 3,
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.email || form.password.length < 6) {
      toast.error("Email and a password (6+ chars) are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/api/admin/demo/accounts", form);
      toast.success("Demo account created");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create.");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="demo-create-form" className="bg-white border border-[#E5E7EB] rounded-2xl p-5 mb-6">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-lg">Create demo account</div>
        <button onClick={onClose} className="p-1 hover:bg-[#F3F4F6] rounded"><X className="w-4 h-4" /></button>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <input data-testid="demo-email" type="email" placeholder="demo@school.com"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
        <input data-testid="demo-password" type="text" placeholder="Password (6+ chars)"
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm font-mono" />
        <input data-testid="demo-name" placeholder="Full name"
          value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="demo-max-logins" type="number" min={1} max={50}
            value={form.max_logins} onChange={(e) => setForm({ ...form, max_logins: +e.target.value })}
            className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" placeholder="Max logins" />
          <input data-testid="demo-expiry-days" type="number" min={1} max={365}
            value={form.expiry_days} onChange={(e) => setForm({ ...form, expiry_days: +e.target.value })}
            className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" placeholder="Expiry days" />
        </div>
      </div>
      <button data-testid="demo-create-submit" onClick={submit} disabled={busy}
        className="btn-primary mt-4 disabled:opacity-50 inline-flex items-center gap-2">
        <UserPlus className="w-4 h-4" /> {busy ? "Creating…" : "Create demo"}
      </button>
    </div>
  );
}

function Row({ d, onAction }) {
  const meta = d.demo_meta || {};
  const usage = meta.usage || {};
  const used = Object.values(usage).reduce((a, b) => a + (b || 0), 0);
  const [showPw, setShowPw] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [invite, setInvite] = useState({ recipient_email: "", recipient_name: "", password: "", custom_message: "" });
  const [extraDays, setExtraDays] = useState(3);
  const [busy, setBusy] = useState("");
  const call = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); onAction(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  return (
    <tr data-testid={`demo-row-${d.id}`} className="border-t border-[#E5E7EB] align-top">
      <td className="py-3 pr-3">
        <div className="font-semibold text-sm">{d.full_name}</div>
        <div className="text-xs text-[#4B5563]">{d.email}</div>
        <div className="text-[10px] text-[#9CA3AF] mt-1">Created {d.created_at?.slice(0,10)}</div>
      </td>
      <td>
        <span data-testid={`demo-status-${d.id}`}
          className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLES[d.status] || ""}`}>
          {d.status.replace("_", " ")}
        </span>
      </td>
      <td className="text-sm text-center">
        <div className="font-bold">{meta.login_count || 0}/{meta.max_logins || 5}</div>
        <div className="text-[10px] text-[#4B5563]">logins</div>
      </td>
      <td className="text-sm">
        {meta.expires_at ? (
          <div className="flex items-center gap-1 text-xs">
            <Clock className="w-3 h-3" /> {new Date(meta.expires_at).toLocaleString()}
          </div>
        ) : <span className="text-xs text-[#9CA3AF]">on first login</span>}
        {meta.first_login_at && (
          <div className="text-[10px] text-[#4B5563] flex items-center gap-1 mt-0.5">
            <LogIn className="w-3 h-3" /> first: {new Date(meta.first_login_at).toLocaleDateString()}
          </div>
        )}
      </td>
      <td className="text-sm">
        <div className="text-xs">
          {used > 0 ? Object.entries(usage).map(([k, v]) => (
            <div key={k}><b>{v}</b> {k}</div>
          )) : <span className="text-[#9CA3AF]">no usage</span>}
        </div>
      </td>
      <td className="text-right">
        <div className="flex flex-wrap gap-1 justify-end">
          <button data-testid={`demo-reset-${d.id}`} disabled={busy === "reset"}
            onClick={() => call("reset", () => api.post(`/api/admin/demo/accounts/${d.id}/reset`)
              .then(() => toast.success("Demo reset — fresh window")))}
            className="text-xs px-2 py-1 rounded border border-[#E5E7EB] hover:bg-[#F3F4F6] inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Reset
          </button>
          <button data-testid={`demo-extend-${d.id}`} disabled={busy === "extend"}
            onClick={() => call("extend", () => api.post(`/api/admin/demo/accounts/${d.id}/extend`,
              { extra_days: extraDays }).then(() => toast.success(`Extended by ${extraDays} days`)))}
            className="text-xs px-2 py-1 rounded border border-[#E5E7EB] hover:bg-[#F3F4F6] inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> +{extraDays}d
          </button>
          <button data-testid={`demo-disable-${d.id}`} disabled={busy === "disable" || meta.disabled}
            onClick={() => call("disable", () => api.post(`/api/admin/demo/accounts/${d.id}/disable`)
              .then(() => toast.success("Demo disabled")))}
            className="text-xs px-2 py-1 rounded border border-[#E5E7EB] hover:bg-[#F3F4F6] inline-flex items-center gap-1 disabled:opacity-40">
            <X className="w-3 h-3" /> Disable
          </button>
          <button data-testid={`demo-rotate-${d.id}`} onClick={() => setShowPw(!showPw)}
            className="text-xs px-2 py-1 rounded border border-[#E5E7EB] hover:bg-[#F3F4F6] inline-flex items-center gap-1">
            <Key className="w-3 h-3" /> Password
          </button>
          <button data-testid={`demo-invite-${d.id}`} onClick={() => setShowInvite(!showInvite)}
            className="text-xs px-2 py-1 rounded border border-[#A7F3D0] text-[#065F46] hover:bg-[#ECFDF5] inline-flex items-center gap-1">
            <Mail className="w-3 h-3" /> Send invite
          </button>
          <button data-testid={`demo-delete-${d.id}`} disabled={busy === "delete"}
            onClick={() => { if (!window.confirm(`Delete demo ${d.email} permanently?`)) return;
              call("delete", () => api.delete(`/api/admin/demo/accounts/${d.id}`)
                .then(() => toast.success("Deleted"))); }}
            className="text-xs px-2 py-1 rounded border border-[#FECACA] text-[#D92D20] hover:bg-[#FEF2F2] inline-flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
        {showPw && (
          <div className="mt-2 flex gap-1 justify-end" data-testid={`demo-pw-form-${d.id}`}>
            <input type="text" placeholder="New password" value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="text-xs border border-[#E5E7EB] rounded px-2 py-1 font-mono" />
            <button onClick={() => call("pw", () => api.post(`/api/admin/demo/accounts/${d.id}/password`,
              { password: newPw }).then(() => { toast.success("Password rotated"); setShowPw(false); setNewPw(""); }))}
              className="text-xs px-2 py-1 rounded bg-[#064E3B] text-white" disabled={newPw.length < 6}>
              Save
            </button>
          </div>
        )}
        {showInvite && (
          <div className="mt-2 p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg text-left max-w-md ml-auto"
            data-testid={`demo-invite-form-${d.id}`}>
            <div className="text-xs font-bold uppercase tracking-widest text-[#065F46] mb-2">Send demo invite</div>
            <div className="grid grid-cols-2 gap-2">
              <input type="email" placeholder="prospect@school.com" value={invite.recipient_email}
                onChange={(e) => setInvite({ ...invite, recipient_email: e.target.value })}
                className="text-xs border border-[#E5E7EB] rounded px-2 py-1" />
              <input placeholder="Their name" value={invite.recipient_name}
                onChange={(e) => setInvite({ ...invite, recipient_name: e.target.value })}
                className="text-xs border border-[#E5E7EB] rounded px-2 py-1" />
              <input type="text" placeholder="Password to share (6+ chars)" value={invite.password}
                onChange={(e) => setInvite({ ...invite, password: e.target.value })}
                className="text-xs border border-[#E5E7EB] rounded px-2 py-1 font-mono col-span-2" />
              <textarea placeholder="Optional personal note…" rows={2} value={invite.custom_message}
                onChange={(e) => setInvite({ ...invite, custom_message: e.target.value })}
                className="text-xs border border-[#E5E7EB] rounded px-2 py-1 col-span-2" />
            </div>
            <div className="flex justify-between items-center mt-2 gap-2">
              <div className="text-[10px] text-[#4B5563]">
                ⚠️ The password you enter here becomes the active password (overwrites prior).
              </div>
              <button data-testid={`demo-invite-send-${d.id}`}
                onClick={() => call("invite", () => api.post(`/api/admin/demo/accounts/${d.id}/send-invite`, invite)
                  .then(({ data }) => {
                    if (data.ok) {
                      toast.success(`Invite sent to ${data.sent_to}`);
                      setShowInvite(false);
                      setInvite({ recipient_email: "", recipient_name: "", password: "", custom_message: "" });
                    } else {
                      toast.error("Email failed (check Resend config)");
                    }
                  }))}
                disabled={!invite.recipient_email || invite.password.length < 6}
                className="text-xs px-3 py-1.5 rounded bg-[#065F46] text-white disabled:opacity-40 inline-flex items-center gap-1">
                <Mail className="w-3 h-3" /> Send
              </button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminDemo() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/demo/accounts");
      setRows(data);
    } catch { toast.error("Could not load demo accounts"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-demo">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-2xl">Demo accounts</h1>
          <p className="text-sm text-[#4B5563]">Secure trial logins for prospects, parents, schools and partners.</p>
        </div>
        <button data-testid="demo-show-create" onClick={() => setShowCreate(!showCreate)}
          className="btn-primary inline-flex items-center gap-2">
          <UserPlus className="w-4 h-4" /> New demo
        </button>
      </div>

      {showCreate && <div className="mt-6"><CreateForm onCreated={() => { setShowCreate(false); load(); }}
        onClose={() => setShowCreate(false)} /></div>}

      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#D92D20]" /> {rows.length} demo account{rows.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-left text-sm" data-testid="admin-demo-table">
            <thead className="text-xs uppercase tracking-widest text-[#4B5563]">
              <tr>
                <th className="py-2 pr-3">Account</th>
                <th>Status</th>
                <th className="text-center">Logins</th>
                <th>Expiry</th>
                <th>Usage</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="py-6 text-center text-[#4B5563]">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="6" className="py-6 text-center text-[#4B5563]">No demo accounts yet.</td></tr>
              )}
              {!loading && rows.map((d) => <Row key={d.id} d={d} onAction={load} />)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 bg-[#FEF3C7] border border-[#FCD34D] rounded-2xl p-4 text-xs text-[#92400E]">
        <b>Default demo:</b> <code className="font-mono">demo@teacher4me.ca</code> · password <code className="font-mono">Demo@12345</code>.
        Rotate the password from the row above before sharing. Demo content is watermarked and certificates are disabled.
      </div>
    </div>
  );
}
